import "../css/Chat.css";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SockJS from "sockjs-client";
import { Stomp } from "@stomp/stompjs";
import axios from "axios";
import dayjs from "dayjs";
import Header from "./Header";

export default function Chat() {
    const [id, setId] = useState("");
    const [isAdmin, setIsAdmin] = useState(false); // ✅ 관리자 여부 상태 추가
    const [isConnected, setIsConnected] = useState(false);
    const [msgs, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");
    const [tooltip, setTooltip] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const [suggestions, setSuggestions] = useState([]);
    const [cursor, setCursor] = useState(0);
    const [banStatus, setBanStatus] = useState(null);
    const [myBanStatus, setMyBanStatus] = useState(null);

    // ✅ 관리자 기능을 위한 상태 추가
    const [selectedUserInfo, setSelectedUserInfo] = useState(null);
    const [isBanMenuOpen, setIsBanMenuOpen] = useState(false);

    const navigate = useNavigate();
    const stompClientRef = useRef(null);
    const msgEndRef = useRef(null);
    const inputRef = useRef(null);
    const sentTooltips = useRef(new Set()); // hover 요청 중복 방지

    // 채팅 금지 옵션 배열
    const banOptions = [
        { label: "1일 채팅 금지", value: 1 },
        { label: "3일 채팅 금지", value: 3 },
        { label: "5일 채팅 금지", value: 5 },
        { label: "7일 채팅 금지", value: 7 },
        { label: "30일 채팅 금지", value: 30 },
        { label: "영구 채팅 금지", value: -1 },
    ];

    useEffect(() => {
        const token = localStorage.getItem("jwtToken");
        if (token) {
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            axios
                .get(`${process.env.REACT_APP_SPRING_API_URL}/api/session-user`)
                .then((res) => {
                    setId(res.data.username);
                    setIsAdmin(res.data.isAdmin); // ✅ 세션에서 isAdmin 정보 가져오기
                })
                .catch(() => {
                    setId("");
                    setIsAdmin(false);
                    navigate("/");
                });
        } else {
            alert("비정상적인 접근이 감지되었습니다.");
            navigate("/");
        }
    }, [navigate]);

    useEffect(() => {
        axios.get(
            `${process.env.REACT_APP_SPRING_API_URL}/api/ban/me`,
            {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("jwtToken")}`
                }
            }
        )
            .then(res => {
                console.log("초기 밴 상태:", res.data);
                setMyBanStatus(res.data);
            })
            .catch(err => {
                console.error(err);
                setMyBanStatus({ banned: false });
            });
    }, []);

    const isBanned = myBanStatus?.banned;

    const connect = () => {
        if (!id?.trim()) return;

        const socket = new SockJS(`${process.env.REACT_APP_SPRING_API_URL}/broadcast`);
        const stompClient = Stomp.over(socket);
        const token = localStorage.getItem("jwtToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        stompClient.connect(
            headers,
            () => {
                setIsConnected(true);

                // 1. 일반 채팅 브로드캐스트 구독
                stompClient.subscribe("/topic/broadcast", (msg) =>
                    setMessages((prev) => [...prev, JSON.parse(msg.body)])
                );

                // 2. 입장/퇴장 메시지 구독
                stompClient.subscribe("/topic/join-leave", (msg) =>
                    setMessages((prev) => [...prev, JSON.parse(msg.body)])
                );

                // 3. 태그(툴팁) 응답 구독
                stompClient.subscribe("/topic/tag-response", (msg) => {
                    const data = JSON.parse(msg.body);
                    setTooltip(`${data.from} • @${data.company} - [${data.prediction}]`);
                });

                // ⭐ 4. 비속어 차단 에러 알림 구독 (개인 채널)
                stompClient.subscribe("/user/topic/errors", (msg) => {
                    const data = JSON.parse(msg.body);
                    alert(data.text); // 백엔드에서 보낸 "욕설은 금지입니다." 출력
                });
                stompClient.subscribe(
                    "/user/queue/ban-status",
                    (msg) => {
                        const data = JSON.parse(msg.body);
                        console.log("밴 상태 수신:", data);
                        setMyBanStatus(data);
                    }
                );

                stompClient.send("/app/join", {}, id);
            },
            (error) => {
                console.error("WebSocket 연결 실패:", error);
                setIsConnected(false);
            }
        );
        stompClientRef.current = stompClient;
    };

    useEffect(() => {
        if (!myBanStatus?.banned) return;
        if (myBanStatus.days === -1) return; // 영구 밴은 타이머 X

        const timer = setInterval(() => {
            setMyBanStatus(prev => {
                if (!prev) return prev;

                let { days, hours, minutes, seconds } = prev;

                if (seconds > 0) seconds--;
                else if (minutes > 0) { minutes--; seconds = 59; }
                else if (hours > 0) { hours--; minutes = 59; seconds = 59; }
                else if (days > 0) { days--; hours = 23; minutes = 59; seconds = 59; }
                else return { ...prev, banned: false };

                return { ...prev, days, hours, minutes, seconds };
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [myBanStatus?.banned]);

    useEffect(() => {
        if (!banStatus?.banned) return;
        if (banStatus.days === -1) return; // 영구밴 제외

        const timer = setInterval(() => {
            setBanStatus(prev => {
                if (!prev) return prev;

                let { days, hours, minutes, seconds } = prev;

                if (seconds > 0) seconds--;
                else if (minutes > 0) { minutes--; seconds = 59; }
                else if (hours > 0) { hours--; minutes = 59; seconds = 59; }
                else if (days > 0) { days--; hours = 23; minutes = 59; seconds = 59; }
                else return { ...prev, banned: false };

                return { ...prev, days, hours, minutes, seconds };
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [banStatus?.banned]);

    const sendMessage = () => {
        if (!stompClientRef.current?.connected || !inputText.trim()) return;
        stompClientRef.current.send(
            "/app/broadcast",
            {},
            JSON.stringify({ from: id, text: inputText, type: "CHAT" })
        );
        setInputText("");
        setSuggestions([]);
    };

    // ✅ 채팅 금지 처리 함수
    const handleBan = async (days) => {
        const period = days === -1 ? "영구" : `${days}일`;
        if (!window.confirm(`${selectedUserInfo.from}님을 ${period} 동안 채팅 금지하시겠습니까?`)) return;

        try {
            await axios.post(`${process.env.REACT_APP_SPRING_API_URL}/api/admin/ban`, {
                targetUsername: selectedUserInfo.from,
                banDays: days,
            });

            // 🔥 여기 핵심
            const res = await axios.get(
                `${process.env.REACT_APP_SPRING_API_URL}/api/admin/check-ban/${selectedUserInfo.from}`
            );

            setBanStatus(res.data); // ✅ 최신 밴 정보로 갱신

            alert(`${selectedUserInfo.from}님에게 ${period} 금지가 적용되었습니다.`);
            setIsBanMenuOpen(false);

        } catch (err) {
            alert("권한이 없거나 처리 중 오류가 발생했습니다.");
            console.error(err);
        }
    };

    useEffect(() => {
        msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [msgs]);

    const handleChange = async (e) => {
        const val = e.target.value;
        setInputText(val);

        const atIndex = val.lastIndexOf("@");
        if (atIndex !== -1) {
            const query = val.slice(atIndex + 1);
            if (query.length > 0) {
                try {
                    const res = await axios.get(`${process.env.REACT_APP_SPRING_API_URL}/api/company/search?q=${query}`);
                    setSuggestions(Array.isArray(res.data) ? res.data : []);
                    setCursor(0);
                } catch (err) {
                    console.error("자동완성 조회 실패:", err);
                    setSuggestions([]);
                }
            } else setSuggestions([]);
        } else setSuggestions([]);
    };

    const handleSelect = (company) => {
        const atIndex = inputText.lastIndexOf("@");
        const newText = inputText.slice(0, atIndex + 1) + company + " ";
        setInputText(newText);
        setSuggestions([]);
        inputRef.current.focus();
    };

    const handleKeyDown = (e) => {
        if (!suggestions || suggestions.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setCursor((prev) => (prev + 1) % suggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setCursor((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (suggestions[cursor]) handleSelect(suggestions[cursor]);
            else sendMessage();
        }
    };

    const renderPreview = () => {
        const parts = inputText.split(/(@[가-힣a-zA-Z0-9]+)/g);

        return parts.map((part, idx) => {
            if (part.startsWith("@")) {
                const company = part.slice(1);
                return (
                    <span
                        key={idx}
                        className="text-blue-600 cursor-pointer relative"
                        onMouseEnter={e => {
                            if (!stompClientRef.current?.connected) return;
                            if (sentTooltips.current.has(company)) return; // 중복 방지

                            const rect = e.target.getBoundingClientRect();
                            setTooltipPos({
                                top: rect.top - 35 < 0 ? rect.bottom + 5 : rect.top - 35,
                                left: rect.left
                            });

                            stompClientRef.current.send("/app/tag", {}, JSON.stringify({ company, username: id }));
                            sentTooltips.current.add(company);
                        }}
                        onMouseLeave={() => setTooltip(null)}
                    >
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    return (
        <div className="flex flex-col h-screen w-full bg-gradient-to-br from-purple-100 via-pink-50 to-yellow-50 p-2 overflow-hidden">
            <div style={{marginTop: "-8px", marginLeft: "-9px", marginBottom: "10px" }}>
                <Header />
            </div>


            <div className="relative flex justify-center items-start flex-1 h-full max-w-6xl mx-auto w-full">
                {/* ✅ 관리자(isAdmin)이고 선택된 유저 정보가 있을 때만 밴 관리 박스 노출 */}
                {isAdmin && selectedUserInfo && (
                    <div
                        style={{
                            position: "absolute",
                            left: "0",
                            top: "50px",
                            width: "350px",
                            background: "white",
                            border: "2px solid black",
                            boxShadow: "5px 5px 0px 0px rgba(0,0,0,1)",
                            zIndex: 100,
                            padding: "15px",
                        }}
                    >
                        <div className="flex justify-between items-center border-b-2 border-black pb-2 mb-4">
                            <span className="font-extrabold text-lg uppercase">NEWSNAKE</span>
                            <button
                                onClick={() => {
                                    setSelectedUserInfo(null);
                                    setIsBanMenuOpen(false);
                                }}
                                className="font-bold hover:text-red-500"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="text-sm">
                                <p className="text-gray-500 mb-1 font-bold">대상 사용자</p>
                                <p className="text-xl font-black">{selectedUserInfo.from}</p>
                                {banStatus && banStatus.banned && (
                                    <p className="text-sm font-bold text-red-600 mt-1">
                                        🚫 채팅 금지 (
                                        {banStatus.days === -1
                                            ? "영구"
                                            : banStatus.days === 0 && banStatus.hours === 0 && banStatus.minutes === 0 && banStatus.seconds === 0
                                                ? "오늘까지"
                                                : `${banStatus.days.toString().padStart(2,'0')}일 ${banStatus.hours.toString().padStart(2,'0')}:${banStatus.minutes.toString().padStart(2,'0')}:${banStatus.seconds.toString().padStart(2,'0')} 남음`
                                        }
                                        )
                                    </p>
                                )}
                                {banStatus && !banStatus.banned && <p className="text-sm font-bold text-green-600 mt-1">✔ 현재 제재 없음</p>}
                            </div>

                            <hr className="border-black" />

                            <div className="relative">
                                <button
                                    onClick={() => setIsBanMenuOpen(!isBanMenuOpen)}
                                    className="w-full flex justify-between items-center px-4 py-2 border-2 border-black bg-yellow-300 font-bold hover:bg-yellow-400 transition-all active:translate-x-1 active:translate-y-1"
                                >
                                    🚫 채팅 금지 설정
                                    <span className="text-lg">{isBanMenuOpen ? "▲" : "▼"}</span>
                                </button>

                                {isBanMenuOpen && (
                                    <div className="mt-2 border-2 border-black bg-white max-h-48 overflow-y-auto shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                                        {banOptions.map((option) => (
                                            <div
                                                key={option.value}
                                                onClick={() => handleBan(option.value)}
                                                className="px-4 py-3 hover:bg-red-500 hover:text-white cursor-pointer border-b-2 border-black last:border-b-0 font-bold text-sm transition-colors"
                                            >
                                                {option.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <p className="text-[10px] text-gray-400 text-center italic">
                                * 금지 처리 시 해당 사용자는 지정된 기간 동안 메시지 전송이 차단됩니다.
                            </p>
                        </div>
                    </div>
                )}

                <div className="flex flex-col w-1xl w-1xl bg-white shadow-xl rounded-xl p-3 h-[85vh] relative border border-gray-100">
                    <div className="flex justify-between items-center mb-3 gap-2">
                        <input type="text" value={id} readOnly className="px-3 py-1 border rounded-md w-1/3 bg-gray-50" />
                        <button
                            onClick={connect}
                            disabled={isConnected}
                            className="px-4 py-1 rounded-md bg-green-500 text-white disabled:bg-gray-300 font-bold"
                        >
                            {isConnected ? "Connected" : "Connect"}
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto mb-2 flex flex-col gap-4 p-1">
                        {msgs.map(({ from, text, createdAt }, idx) => (
                            <div key={idx} className={`flex flex-col ${from === id ? "items-end" : "items-start"}`}>
                                <div className="mb-1">
                                    <span
                                        onClick={async () => {
                                            // ✅ 관리자가 아니거나 본인 아이디인 경우 클릭 이벤트 무시
                                            if (!isAdmin || from === id) return;

                                            const info = {
                                                from,
                                                time: createdAt ? dayjs(createdAt).format("HH:mm:ss") : "N/A"
                                            };

                                            setSelectedUserInfo(info);
                                            setIsBanMenuOpen(false);

                                            try {
                                                const res = await axios.get(
                                                    `${process.env.REACT_APP_SPRING_API_URL}/api/admin/check-ban/${from}`
                                                );
                                                setBanStatus(res.data);
                                            } catch (e) {
                                                setBanStatus(null);
                                            }
                                        }}
                                        // ✅ 관리자일 때만 커서 포인터와 언더라인 효과 부여
                                        className={`text-xs font-bold bg-gray-100 px-2 py-0.5 rounded-full cursor-pointer hover:underline"`}
                                    >
                                        {from} • {createdAt ? dayjs(createdAt).format("HH:mm:ss") : ""}
                                    </span>
                                </div>
                                <div
                                    className={`p-3 rounded-2xl max-w-[90%] break-words shadow-sm animate-fade-in ${
                                        from === id ? "bg-green-200 text-right" : "bg-gray-100 text-left"
                                    }`}
                                >
                                    <div className="text-sm">{text}</div>
                                </div>
                            </div>
                        ))}
                        <div ref={msgEndRef}></div>
                    </div>
                    {/* 입력 미리보기 */}
                    <div className="mt-2 p-2 border rounded-lg h-12 overflow-x-auto bg-gray-50">
                        {renderPreview()}
                    </div>
                    <div className="relative flex gap-2 items-end">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                value={inputText}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                ref={inputRef}
                                disabled={!isConnected || isBanned}
                                placeholder={
                                    !isConnected
                                        ? "연결 필요"
                                        : isBanned
                                            ? myBanStatus.days === -1
                                                ? "영구적으로 채팅이 금지되었습니다"
                                                : `채팅 금지 ${myBanStatus.days}일 ${myBanStatus.hours}:${myBanStatus.minutes}:${myBanStatus.seconds} 남음`
                                            : "메시지를 입력하세요."
                                }
                                className="w-full px-4 py-2 border rounded-2xl"
                            />
                            {/* 자동완성 후보 박스 */}
                            {Array.isArray(suggestions) && suggestions.length > 0 && (
                                <ul className="absolute bg-white border shadow-md max-h-52 overflow-y-auto mt-1 w-full z-50 rounded-lg">
                                    {suggestions.map((c, idx) => (
                                        <li
                                            key={c}
                                            className={`px-3 py-2 cursor-pointer ${
                                                idx === cursor
                                                    ? "bg-blue-300 font-bold"
                                                    : "hover:bg-blue-100"
                                            }`}
                                            onMouseEnter={() => setCursor(idx)}
                                            onMouseDown={() => handleSelect(c)}
                                        >
                                            {c}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <button
                            onClick={sendMessage}
                            className="px-5 py-2 rounded-2xl bg-purple-500 text-white font-bold hover:bg-purple-600 transition-colors"
                        >
                            Send
                        </button>
                    </div>
                </div>


            </div>

            <style>{`
                @keyframes fade-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fade-in { animation: fade-in 0.3s ease-out; }
                div::-webkit-scrollbar { width: 6px; }
                div::-webkit-scrollbar-thumb { background-color: rgba(0,0,0,0.2); border-radius: 10px; }
            `}</style>
        </div>
    );
}